/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Column widths for the location/sensor list, from the width available to it.
 */

export interface ColumnWidths {
  name: number
  temperature: number
  humidity: number
}

export const DEFAULT_COLUMNS: ColumnWidths = { name: 200, temperature: 75, humidity: 75 }
const MIN_COLUMNS: ColumnWidths = { name: 150, temperature: 50, humidity: 50 }
const MAX_READING = 100

const total = (c: ColumnWidths) => c.name + c.temperature + c.humidity

/** The blast freezer list's time-in-range column, on top of the three that flex. */
export const TIMING_WIDTH = 80

/** The alert symbol column, on top of the three that flex. */
export const ALERT_WIDTH = 32

/** The list's default width: the default columns exactly. */
export const DEFAULT_LIST_WIDTH = total(DEFAULT_COLUMNS)
/** Below this the list scrolls sideways instead of squeezing further. */
export const MIN_LIST_WIDTH = total(MIN_COLUMNS)

/**
 * Widths that fill `available`. Wider than the default, the reading columns grow first,
 * to 100px each, and the name takes the rest. Narrower, all three shrink toward their
 * minimums in proportion to what each can give up; at the minimums the table stops
 * shrinking and the panel scrolls.
 */
export const columnWidths = (available: number): ColumnWidths => {
  const width = Math.max(available, MIN_LIST_WIDTH)
  if (width >= DEFAULT_LIST_WIDTH) {
    const extra = width - DEFAULT_LIST_WIDTH
    const reading = Math.min(MAX_READING, DEFAULT_COLUMNS.temperature + Math.floor(extra / 2))
    return { name: width - 2 * reading, temperature: reading, humidity: reading }
  }
  const deficit = DEFAULT_LIST_WIDTH - width
  const room = DEFAULT_LIST_WIDTH - MIN_LIST_WIDTH
  const give = (key: keyof ColumnWidths) => Math.round((deficit * (DEFAULT_COLUMNS[key] - MIN_COLUMNS[key])) / room)
  const temperature = DEFAULT_COLUMNS.temperature - give('temperature')
  const humidity = DEFAULT_COLUMNS.humidity - give('humidity')
  return { name: width - temperature - humidity, temperature, humidity }
}
